<template>
  <v-app
    :light="isLightTheme"
    :dark="!isLightTheme"
    :key="$i18n.locale"
  >
    <v-toolbar app style="z-index: 100;">
      <v-toolbar-title class="headline text-uppercase">
        <img v-if="logo" :src="logo" style="display: inline-block; margin-right: 8px; max-height: 48px; vertical-align: middle"/>
        <span v-html="title"></span>
      </v-toolbar-title>
      <v-spacer></v-spacer>
      <v-btn
        v-if="hasSession && cartEnabled"
        @click="currentView = 'Cart'"
        color="info"
        outline
        :disabled="false"
        style="min-width: 40px; padding: 0"
      >
        <v-badge right color="orange" :value="itemsCount > 0">
          <span slot="badge">{{ this.itemsCount }}</span>
          <v-icon>shopping_basket</v-icon>
        </v-badge>
      </v-btn>
      <v-btn icon color="info" outline @click.stop="rightDrawer = !rightDrawer" v-if="hasSession && !$root.isEmbedded">
        <img v-if="avatar" :src="avatar" alt="Account">
        <v-icon v-else dark>account_circle</v-icon>
      </v-btn>
      <v-btn v-for="lang in languageIdList" icon :color="currentLangId === lang ? 'info' : ''" outline @click.stop="$i18n.locale = lang" v-if="!hasSession && languageIdList.length > 1">
          <v-img
                  :src="flag(lang)"
                  width="24"
                  height="16"
                  style="max-width: 24px; border-radius: 0"
          />
      </v-btn>
    </v-toolbar>

    <v-content :class="`${!hasSession && !authorizing ? 'login' : ''}`">
      <Login
              @login="login"
              v-if="!hasSession && !authorizing"
              :with-instance="$root.config.login && $root.config.login.requireInstance"
              :intro="$root.config.login && $root.config.login.intro"
              :outro="$root.config.login && $root.config.login.outro"
      />
      <template v-else-if="!authorizing">
        <Cart v-if="currentView === 'Cart' && cartEnabled" :items="items" @clear="clearCart"/>
        <component
          v-if="!!currentView && currentView.name && (!currentView.pinProtected || pinPassed)"
          v-bind:is="currentView.name"
          :key="currentView.name"
          :config="currentView.props"
        ></component>
        <div style="height: 50px"></div>
      </template>

      <footer v-html="$t(footer)" v-if="footer && hasSession"></footer>
    </v-content>

    <v-navigation-drawer
      temporary
      :right="true"
      v-model="rightDrawer"
      fixed
      app
      style="z-index:205"
    >
      <v-toolbar flat>
        <v-list>
          <v-list-tile>
            <v-list-tile-title class="title">{{ $t('Application.application') }}</v-list-tile-title>
          </v-list-tile>
        </v-list>
      </v-toolbar>

      <v-divider></v-divider>

      <v-list dense class="pt-0">
        <v-list-tile v-for="lang in languageIdList"  @click="$i18n.locale = lang" v-if="languageIdList.length > 1">
          <v-list-tile-action>
            <v-img
              :src="flag(lang)"
              width="24"
              height="16"
              style="max-width: 24px"
            />
          </v-list-tile-action>
          <v-list-tile-content>
            <v-list-tile-title :style="`font-weight: ${currentLangId === lang ? 'bold' : 'normal'}`">{{ $t('Lang.name', lang) }}</v-list-tile-title>
          </v-list-tile-content>
        </v-list-tile>
        <hr/>
        <v-list-tile v-if="changePinAvailable" @click="changePin">
          <v-list-tile-action>
            <v-icon>fiber_pin</v-icon>
          </v-list-tile-action>
          <v-list-tile-content>
            <v-list-tile-title>{{ $t('ChangePin.change') }}</v-list-tile-title>
          </v-list-tile-content>
        </v-list-tile>
        <v-list-tile v-if="changePasswordAvailable" @click="changePassword">
          <v-list-tile-action>
            <v-icon>vpn_key</v-icon>
          </v-list-tile-action>
          <v-list-tile-content>
            <v-list-tile-title>{{ $t('ChangePassword.change') }}</v-list-tile-title>
          </v-list-tile-content>
        </v-list-tile>
        <v-list-tile v-if="$root.canLogout" @click="logout">
          <v-list-tile-action>
            <v-icon>exit_to_app</v-icon>
          </v-list-tile-action>
          <v-list-tile-content>
            <v-list-tile-title>{{ $t('Login.logout') }}</v-list-tile-title>
          </v-list-tile-content>
        </v-list-tile>
        <v-list-tile v-if="!$root.canLogout && $root.hasSession" @click="logout">
          <v-list-tile-action>
            <v-icon>exit_to_app</v-icon>
          </v-list-tile-action>
          <v-list-tile-content>
            <v-list-tile-title>{{ $t('Login.login') }}</v-list-tile-title>
          </v-list-tile-content>
        </v-list-tile>
        <div class="version">Portal: {{versions.portal || '-'}}</div>
        <div class="version">Instance: {{versions.instance || '-'}}</div>
      </v-list>
    </v-navigation-drawer>
    <v-bottom-nav
      v-if="hasSession && hasBottomTabs()"
      :active="getCurrentViewName()"
      :value="true"
      :fixed="true"
    >
      <v-btn
        v-for="item in components()"
        v-if="(!item.access || $root.canAccess(item.access))"
        color="teal"
        flat
        :key="item.name"
        :value="item.name"
        @click="() => onTabChange(item.name)"
      >
        <span>{{ $t(item.title) }}</span>
        <v-icon>{{item.icon}}</v-icon>
      </v-btn>

    </v-bottom-nav>

    <v-dialog
      v-model="pinDialog"
      persistent width="200"
      :dark="!isLightTheme"
    >
      <PinInput
        @action="onPinInputAction"
        :pinLength="4"
        :active="pinDialog"
      ></PinInput>
    </v-dialog>

    <v-dialog
      v-model="changePinDialog"
      persistent
      width="400"
      :dark="!isLightTheme"
    >
      <ChangePinInput
        @action="onChangePinAction"
      >
      </ChangePinInput>
    </v-dialog>

    <v-dialog
      v-model="changePasswordDialog"
      persistent
      width="400"
      :dark="!isLightTheme"
    >
      <ChangePasswordInput
        @action="onChangePasswordAction"
      >
      </ChangePasswordInput>
    </v-dialog>

    <!-- Error message -->
    <v-snackbar v-model="pinError" color="error" :timeout="5000" top>
      {{$t('Invoices.invalidPinCode')}}
      <v-btn dark flat @click="pinError = false">{{$t('Action.close')}}</v-btn>
    </v-snackbar>

  </v-app>
</template>

<script>
  import Cart from './modules/Cart';
  import Login from './components/Login';
  import PinInput from './components/PinInput';
  import ChangePinInput from './components/ChangePinInput';
  import ChangePasswordInput from './components/ChangePasswordInput';
  import VList from 'vuetify/lib/components/VList/VList'
  import VListTile from 'vuetify/lib/components/VList/VListTile'

  import RequiresResolver from './mixins/RequiresResolver';

  export default {
    name: 'App',
    mixins: [RequiresResolver],
    components: {
      VListTile,
      VList,
      Cart,
      Login,
      PinInput,
      ChangePinInput,
      ChangePasswordInput,
    },
    mounted: function () {
      this.$root.$on('activate-tab', (tabName) => {
        this.currentView = this.getComponent(tabName);
      });
    },
    data() {
      const currentView = this.mainComponent();
      return {
        rightDrawer: false,
        currentView: currentView,
        newView: undefined,
        items: {},
        pinPassed: false,
        pinDialog: false,
        changePinDialog: false,
        changePasswordDialog: false,
        pinError: false,
        logo: this.$root.config.logo,
        title: this.$root.config.title,
      };
    },
    props: {
      session: {
        type: Object
      }
    },
    computed: {
      cartEnabled() {
        const cart = this.$root.config.cart;
        return cart === true || (typeof cart === 'object' && this.meetsRequirements({requires: cart}));
      },
      changePinAvailable() {
        return this.components().some(x => x.pinProtected);
      },
      changePasswordAvailable() {
        return this.$root.config.canChangePassword || false;
      },
      isLightTheme() {
        return this.$root.config.theme === 'light';
      },
      footer() {
        return this.$root.config.layout && this.$root.config.layout.footer;
      },
      hasSession() {
        return this.$root.hasSession;
      },
      authorizing() {
        return this.$root.authorizing;
      },
      avatar() {
        return this.$root.avatar;
      },
      itemsCount() {
        Object.keys(this.items)
          .filter(key => this.items[key] === 0)
          .forEach(i => delete this.items[i]);
        return Object.keys(this.items).reduce((p,c) => p + this.items[c], 0);
      },
      languageIdList() {
        return Object.keys(this.$i18n.messages);
      },
      currentLangId() {
        return this.$i18n.locale;
      },
      nextLanguageId() {
        const languages = this.languageIdList;
        if (!languages || languages.length < 2) {
          return languages[0] || '';
        }
        const index = languages.indexOf(this.currentLangId);
        return languages[(index + 1) % languages.length];
      },
      versions() {
        return this.$root.versions || {};
      }
    },
    methods: {
      flag(langId) {
        switch (langId) {
          case 'de': return require(`./assets/flag-de.svg`);
          case 'en': return require(`./assets/flag-en.svg`);
          case 'gb': return require(`./assets/flag-gb.svg`);
        }
        return '';
      },
      mainComponent() {
        const components = this.components();
        return components.find(x => x.main) || components[0];
      },
      hasBottomTabs() {
        return this.components().length > 1;
      },
      components() {
        const items = this.$root.config.menu;
        const res = items.filter(_ => this.meetsRequirements(_));
        return res;
      },
      getCurrentViewName() {
        const name = (this.currentView && this.currentView.name
          ? this.currentView.name
          : this.currentView);
        return (name === 'Cart' ? 'shop' : name) || 'unknown';
      },
      getComponent(name) {
        return this.components().find(x => x.name === name);
      },
      hasMenuComponent(name) {
        return this.$root.config.menu.some(item => item.component === name);
      },
      login(session, persist) {
        this.$root.registerSession(session, persist);
        this.currentView = this.mainComponent();
        this.$router.replace('/');
      },
      logout() {
        this.rightDrawer = false;
        this.$root.clearSession();
        this.clearCart();
        this.pinPassed = false;
        this.currentView = this.mainComponent();
        this.$router.push('/login');
      },
      clearCart() {
        this.items = {};
      },
      addToCart(items) {
        Object.keys(items).forEach(itemnum => {
          this.$set(this.items, itemnum, ((this.items || {})[itemnum] || 0) + items[itemnum]);
        });
      },
      changePin() {
        this.changePinDialog = true;
      },
      onPinInputAction(action) {
        this.pinDialog = false;

        if (action === 'success') {
          this.pinPassed = true;
          this.currentView = this.newView;
        } else if (action === 'close') {
          this.refreshView();
        } else {
          this.refreshView();
          this.pinError = true;
        }
      },
      onChangePinAction(changeResult) {
        this.changePinDialog = false;
      },
      changePassword() {
        this.changePasswordDialog = true;
      },
      onChangePasswordAction(changeResult) {
        this.changePasswordDialog = false;
        if (changeResult === 'success') {
          this.logout();
        }
      },
      refreshView() {
        const lastView = this.currentView;
        this.currentView = undefined;
        this.$nextTick(() => this.currentView = lastView);
      },

      onTabChange(name) {
        const componentConf = this.getComponent(name);
        if (componentConf) {
          if (componentConf.pinProtected && !this.pinPassed) {
            this.pinDialog = true;
            this.newView = componentConf;
          } else {
            this.currentView = componentConf;
          }
        }
      }
    }
  }
</script>
<style>
  .version {
    width: 100%;
    text-align: left;
    padding-left: 16px;
    opacity: .3;
    font-size: 10px;
  }

  footer {
    padding: 16px
  }

</style>
